/**
 * Turning free-text delivery contact details into actionable links (WMS-38).
 * Both helpers return `null` when there's nothing sensible to link, so the
 * caller renders plain text rather than a link that misdials or opens an empty
 * map.
 */

/**
 * Separators people use when they cram two numbers into one field. The word
 * forms take `\s+` on both sides rather than a literal space, so a tab, a
 * newline, a doubled space or a non-breaking space (all matched by `\s`, which
 * pasted text is full of) separate just as well.
 */
const PHONE_SPLIT = /[,;/]|\s+(?:arba|ir|or|and)\s+/i;

/** E.164 caps a real number at 15 digits; more than that is two of them. */
const MAX_DIGITS = 15;
/** Below this it isn't a phone number — "n/a", a house number, a bare extension. */
const MIN_DIGITS = 7;

/**
 * An extension tacked onto the number: `ext 12`, `ext. 12`, `x12`, `vid. 12`
 * (LT *vidaus*), `#12`. Captured deliberately rather than swept into the dial
 * string, where its digits would silently become part of the number.
 */
const EXTENSION = /(?:^|[\s(])(?:(?:ext|extension|x|vid|vidaus)\.?|#)\s*(\d{1,6})\b/i;

/**
 * `tel:` target for a phone number typed by a human.
 *
 * Only the first number is linked when the field holds several — concatenating
 * "+370 600 11111 / +370 600 22222" into one dial string would call nobody.
 * Formatting characters are dropped and a leading `+` kept. An extension is
 * pulled out and re-attached as RFC 3966 `;ext=`; a dialer that doesn't
 * understand the parameter falls back to the main number, never to a different
 * one.
 *
 * Splitting can never be sufficient on its own — a bare space between two
 * numbers is indistinguishable from a space *inside* one — so the chosen
 * segment is then checked and **fails closed**. It gets no link at all if it
 * carries a second `+`, more digits than E.164 permits, or two whitespace-
 * separated groups that are each long enough to be a number on their own
 * ("1234567 7654321"). A missing link is a nuisance; a wrong call is worse.
 */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const first = phone.split(PHONE_SPLIT)[0] ?? '';
  const trimmed = first.trim();
  if (!trimmed) return null;

  // Lift the extension out before anything counts digits.
  const extMatch = EXTENSION.exec(trimmed);
  const extension = extMatch?.[1] ?? null;
  const base = extMatch ? trimmed.replace(extMatch[0], ' ') : trimmed;

  if ((base.match(/\+/g)?.length ?? 0) > 1) return null;
  // Two groups that each hold a whole number's worth of digits are two numbers,
  // whatever the total. A single number split for readability ("+370 600 12345")
  // has no such group.
  const fullSizedGroups = base
    .split(/\s+/)
    .filter((g) => g.replace(/\D/g, '').length >= MIN_DIGITS);
  if (fullSizedGroups.length > 1) return null;

  // A `+` anywhere ahead of the digits is this number's country prefix —
  // "Tel. +370…" should keep it, not silently dial a national number.
  const plus = /^\D*\+/.test(base);
  const digits = base.replace(/\D/g, '');
  if (digits.length < MIN_DIGITS || digits.length > MAX_DIGITS) return null;
  return `tel:${plus ? '+' : ''}${digits}${extension ? `;ext=${extension}` : ''}`;
}

/**
 * Google Maps *navigation* target for a delivery address — Jonas asked for
 * directions, not a pin, so this uses the directions endpoint.
 *
 * Addresses are entered in a multi-line textarea; newlines become ", " so the
 * query reads as one address line.
 */
export function mapsHref(address: string | null | undefined): string | null {
  if (!address) return null;
  const oneLine = address
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .join(', ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!oneLine) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(oneLine)}`;
}
